import { useEffect, useState, useCallback } from "react";
import { api, formatApiError, inr, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { toast, Toaster } from "sonner";
import { useNavigate } from "react-router-dom";
import { ArrowsClockwise, HandCoins, Users, Hammer, Receipt, Coins, CaretRight, Bank, Wallet } from "@phosphor-icons/react";

/**
 * Pending payouts hub.
 *
 * Six filter cards at the top (Grand total + 5 queues). Click any card to
 * narrow the list below. Each row has a bold "Open" button that deep-links
 * to the exact tab where the accountant can act on it, so there's never a
 * second click to find the right place.
 */
const SECTIONS = [
    { id: "payroll", label: "Payroll", icon: HandCoins,
      route: () => "/payroll",
      hint: <><Bank size={11} className="inline mr-1" /> Bank payouts auto-map from statement upload. Cash/Fiat: click Pay.</> },
    { id: "contract_payouts", label: "Contract payouts", icon: Hammer,
      route: () => "/contract-payouts",
      hint: <><Bank size={11} className="inline mr-1" /> Bank payouts auto-map from statement upload. Cash/Fiat: click Pay.</> },
    { id: "advances", label: "Staff advances", icon: Users,
      route: () => "/employee-advances",
      hint: "Bank disburse: upload statement. Cash/Fiat: use Staff advances page." },
    { id: "petty_cash", label: "Petty cash", icon: Coins,
      route: () => "/petty-cash?tab=disburse",
      hint: "Disburse from Petty Cash → To disburse tab." },
    { id: "expense_claims", label: "Expense claims", icon: Receipt,
      route: (id) => `/expense-claims?tab=disburse&open=${id}`,
      hint: "Reimburse from Expense claims page." },
];


export default function PendingPayouts() {
    const [data, setData] = useState({ payroll: [], contract_payouts: [], advances: [], petty_cash: [], expense_claims: [], counts: {}, totals: {} });
    const [loading, setLoading] = useState(false);
    // null = show everything; otherwise show only the picked section.
    const [filter, setFilter] = useState(null);
    const navigate = useNavigate();

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/pending-payouts");
            setData(data);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { refresh(); }, [refresh]);

    const totals = data.totals || {};
    const counts = data.counts || {};
    const sectionVisible = (id) => !filter || filter === id;

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader title="Pending payouts"
                sub={<>Everyone waiting to be paid, in one queue. Click any card to narrow the view, then hit <b>Open</b> on a row to jump straight to where you can disburse it.</>}
                right={
                    <Button variant="outline" onClick={refresh} disabled={loading} data-testid="pp-refresh">
                        <ArrowsClockwise size={14} className="mr-2" /> Refresh
                    </Button>
                } />

            <div className="p-8 space-y-6">
                {/* Filter cards — click to narrow. Click again (or the Grand card) to reset. */}
                <div className="grid grid-cols-6 gap-3">
                    <FilterCard
                        testid="pp-grand"
                        label="Grand total" icon={Wallet}
                        amount={totals.grand_total || 0}
                        count={counts.total || 0}
                        active={filter === null}
                        onClick={() => setFilter(null)}
                        accent="#f97316"
                    />
                    {SECTIONS.map((s) => (
                        <FilterCard
                            key={s.id}
                            testid={`pp-card-${s.id}`}
                            label={s.label} icon={s.icon}
                            amount={totals[s.id] || 0}
                            count={counts[s.id] || 0}
                            active={filter === s.id}
                            onClick={() => setFilter(filter === s.id ? null : s.id)}
                        />
                    ))}
                </div>

                {sectionVisible("payroll") && (
                    <PayrollList rows={data.payroll} navigate={navigate} />)}
                {sectionVisible("contract_payouts") && (
                    <ContractPayoutList rows={data.contract_payouts} navigate={navigate} />)}
                {sectionVisible("advances") && (
                    <AdvancesList rows={data.advances} navigate={navigate} />)}
                {sectionVisible("petty_cash") && (
                    <PettyCashList rows={data.petty_cash} navigate={navigate} />)}
                {sectionVisible("expense_claims") && (
                    <ExpenseClaimsList rows={data.expense_claims} navigate={navigate} />)}

                {/* Empty state when a filter is picked but has no rows */}
                {filter && counts[filter] === 0 ? (
                    <div className="bg-white border rounded-md p-10 text-center text-stone-500"
                         style={{ borderColor: "var(--se-border)" }}
                         data-testid="pp-empty-filter">
                        Nothing pending in this queue. Clear the filter to see other payouts.
                    </div>
                ) : null}
            </div>
        </>
    );
}


function FilterCard({ testid, label, icon: Icon, amount, count, active, onClick, accent }) {
    const activeStyles = accent
        ? { borderColor: accent, background: "#fff7ed", color: "#c2410c" }
        : { borderColor: "var(--se-brand)", background: "#fff7ed" };
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`text-left border rounded-md px-4 py-3 transition-all duration-150 ${active ? "shadow-sm" : "bg-white hover:bg-stone-50 hover:border-stone-400"}`}
            style={active ? activeStyles : { borderColor: "var(--se-border)" }}
        >
            <div className={`flex items-center gap-2 text-[11px] uppercase tracking-widest ${active && accent ? "" : "text-stone-500"}`}>
                <Icon size={13} /> {label}
            </div>
            <div className="se-num text-2xl mt-1 font-medium" style={active && accent ? { color: accent } : { color: "#0f172a" }}>
                ₹ {inr(amount)}
            </div>
            <div className="text-[11px] text-stone-500 mt-0.5">
                {count} {label === "Grand total" ? (count === 1 ? "item" : "items") : "pending"}
            </div>
        </button>
    );
}


function OpenBtn({ onClick, testid }) {
    return (
        <Button
            size="sm"
            onClick={onClick}
            style={{ background: "var(--se-brand)", color: "#fff" }}
            className="shadow-sm hover:opacity-90"
            data-testid={testid}
        >
            Open <CaretRight size={12} className="ml-1" />
        </Button>
    );
}


function SectionShell({ title, icon: Icon, count, totalLabel, total, hint, children }) {
    if (count === 0) return null;
    return (
        <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
            <div className="px-5 py-3 border-b flex items-center gap-3" style={{ borderColor: "var(--se-border)" }}>
                <Icon size={16} />
                <div className="font-serif text-lg mr-auto">{title} — {count} pending · ₹ {inr(total)} {totalLabel}</div>
                {hint ? <span className="text-[11px] text-stone-500">{hint}</span> : null}
            </div>
            {children}
        </div>
    );
}


function PayrollList({ rows, navigate }) {
    const section = SECTIONS.find((s) => s.id === "payroll");
    return (
        <SectionShell title="Payroll" icon={HandCoins} count={rows.length}
            total={rows.reduce((s, r) => s + (r.outstanding || 0), 0)} totalLabel="outstanding"
            hint={section.hint}>
            <table className="se-table" data-testid="pp-payroll-table">
                <thead><tr><th>Employee</th><th>Month</th><th className="text-right">Net</th><th className="text-right">Paid</th><th className="text-right">Outstanding</th><th></th></tr></thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.id} data-testid={`pp-payroll-row-${r.id}`}>
                            <td>{r.employee_name}<div className="text-[10px] text-stone-500">{r.employee_code}</div></td>
                            <td>{r.month}</td>
                            <td className="se-num text-right">₹ {inr(r.net_payable)}</td>
                            <td className="se-num text-right">₹ {inr(r.amount_paid || 0)}</td>
                            <td className="se-num text-right font-medium text-amber-700">₹ {inr(r.outstanding)}</td>
                            <td className="text-right"><OpenBtn onClick={() => navigate(section.route(r.id))} testid={`pp-payroll-open-${r.id}`} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </SectionShell>
    );
}


function ContractPayoutList({ rows, navigate }) {
    const section = SECTIONS.find((s) => s.id === "contract_payouts");
    return (
        <SectionShell title="Contract payouts" icon={Hammer} count={rows.length}
            total={rows.reduce((s, r) => s + (r.outstanding || 0), 0)} totalLabel="outstanding"
            hint={section.hint}>
            <table className="se-table" data-testid="pp-cp-table">
                <thead><tr><th>Worker</th><th>Month</th><th className="text-right">Net</th><th className="text-right">Paid</th><th className="text-right">Outstanding</th><th></th></tr></thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.id} data-testid={`pp-cp-row-${r.id}`}>
                            <td>{r.worker_name}<div className="text-[10px] text-stone-500">{r.worker_code}</div></td>
                            <td>{r.month}</td>
                            <td className="se-num text-right">₹ {inr(r.net_payable)}</td>
                            <td className="se-num text-right">₹ {inr(r.amount_paid || 0)}</td>
                            <td className="se-num text-right font-medium text-amber-700">₹ {inr(r.outstanding)}</td>
                            <td className="text-right"><OpenBtn onClick={() => navigate(section.route(r.id))} testid={`pp-cp-open-${r.id}`} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </SectionShell>
    );
}


function AdvancesList({ rows, navigate }) {
    const section = SECTIONS.find((s) => s.id === "advances");
    return (
        <SectionShell title="Staff advances" icon={Users} count={rows.length}
            total={rows.reduce((s, r) => s + (r.amount || 0), 0)} totalLabel="approved"
            hint={section.hint}>
            <table className="se-table" data-testid="pp-adv-table">
                <thead><tr><th>Employee</th><th className="text-right">Amount</th><th>Reason</th><th>Approved</th><th></th></tr></thead>
                <tbody>
                    {rows.map((a) => (
                        <tr key={a.id} data-testid={`pp-adv-row-${a.id}`}>
                            <td>{a.employee_name}<div className="text-[10px] text-stone-500">{a.employee_code}</div></td>
                            <td className="se-num text-right font-medium">₹ {inr(a.amount)}</td>
                            <td className="text-xs max-w-md truncate" title={a.reason}>{a.reason}</td>
                            <td className="text-xs">{formatDate(a.approved_at)}</td>
                            <td className="text-right"><OpenBtn onClick={() => navigate(section.route(a.id))} testid={`pp-adv-open-${a.id}`} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </SectionShell>
    );
}


function PettyCashList({ rows, navigate }) {
    const section = SECTIONS.find((s) => s.id === "petty_cash");
    return (
        <SectionShell title="Petty cash" icon={Coins} count={rows.length}
            total={rows.reduce((s, r) => s + (r.amount || 0), 0)} totalLabel="approved"
            hint={section.hint}>
            <table className="se-table" data-testid="pp-pc-table">
                <thead><tr><th>Employee</th><th className="text-right">Amount</th><th>Reason</th><th>Approved</th><th></th></tr></thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.id} data-testid={`pp-pc-row-${r.id}`}>
                            <td>{r.employee_name}<div className="text-[10px] text-stone-500 capitalize">{r.employee_role}</div></td>
                            <td className="se-num text-right font-medium">₹ {inr(r.amount)}</td>
                            <td className="text-xs max-w-md truncate" title={r.reason}>{r.reason}</td>
                            <td className="text-xs">{formatDate(r.approved_at)}</td>
                            <td className="text-right"><OpenBtn onClick={() => navigate(section.route(r.id))} testid={`pp-pc-open-${r.id}`} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </SectionShell>
    );
}


function ExpenseClaimsList({ rows, navigate }) {
    const section = SECTIONS.find((s) => s.id === "expense_claims");
    return (
        <SectionShell title="Expense claims" icon={Receipt} count={rows.length}
            total={rows.reduce((s, r) => s + (r.total_amount || 0), 0)} totalLabel="approved"
            hint={section.hint}>
            <table className="se-table" data-testid="pp-ec-table">
                <thead><tr><th>Claim #</th><th>Claimant</th><th className="text-right">Amount</th><th>Reason</th><th>Approved</th><th></th></tr></thead>
                <tbody>
                    {rows.map((c) => (
                        <tr key={c.id} data-testid={`pp-ec-row-${c.id}`}>
                            <td className="font-mono text-xs">{c.claim_no}</td>
                            <td>{c.claimant_name}<div className="text-[10px] text-stone-500 capitalize">{c.claimant_role || ""}</div></td>
                            <td className="se-num text-right font-medium">₹ {inr(c.total_amount)}</td>
                            <td className="text-xs max-w-md truncate" title={c.reason}>{c.reason}</td>
                            <td className="text-xs">{formatDate(c.approved_at)}</td>
                            <td className="text-right"><OpenBtn onClick={() => navigate(section.route(c.id))} testid={`pp-ec-open-${c.id}`} /></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </SectionShell>
    );
}
